import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { HAFHOwnerSeed } from "./data/HAFHSeed";

import HAFHAttendanceDisplay from "./components/HAFHAttendanceDisplay";
import HAFHMessagesDisplay from "./components/HAFHMessagesDisplay";
import HAFHDosDont from "./components/HAFHDosDont";
import HAFHContatList from "./components/HAFHContactList";
import HAFHActivityList from "./components/HAFHActivityList";
import HAFHRoutineList from "./components/HAFHRoutineList";
import HAFHMessMenuList from "./components/HAFHMessMenuList";
import HAFHTransportList from "./components/HAFHTransportList";
import HAFHPhotos from "./components/HAFHPhotos";
import HAFHStudentRankings from "./components/HAFHStudentRankings";
import HAFHOnboarding from "./components/HAFHOnboarding";

import HAFHAI from "./HAFHAI";

const demoHostelCode = "THINK5WHYS_HOSTEL_001";

const TAB_ICONS = {
  signal: "🧠",
  messages: "💬",
  contacts: "📞",
  rca: "🔍",
  applause: "👏",
};

const ADMIN_TABS = [
  "signal",
  "messages",
  "contacts",
  "rca",
  "applause",
];

const HAFHAdmin = ({
  t,
  selectedLanguage,
  currentHostel,
  role,
  currentTheme,
}) => {
  const authState = useSelector((state) => state.authReducer || {});
  const navigate = useNavigate();

  const userRole = role;

  const hostelCode = demoHostelCode;

  const data =
    HAFHOwnerSeed[selectedLanguage] ||
    HAFHOwnerSeed["en"];

  const [activeTab, setActiveTab] = useState("signal");

  const student =
    data?.hostels?.[0]?.students?.[0];

  // =====================================================
  // COMMON THEME HELPERS
  // =====================================================

  const cardClass = `
    ${currentTheme.card}
    ${currentTheme.text}
    border
    ${currentTheme.border}
    rounded-2xl
    p-5
    shadow-lg
  `;

  const titleClass = `
    ${currentTheme.accent}
    text-lg
    font-semibold
  `;

  const paragraphClass = `
    ${currentTheme.subText}
    text-xs
    lg:text-sm
    leading-relaxed
  `;

  const buttonClass = `
    mt-8
    text-xs
    lg:text-sm
    px-4
    py-2
    rounded-xl
    transition-all
    ${currentTheme.button}
    ${currentTheme.buttonHover}
  `;

  // =====================================================
  // ADMIN AI INSIGHTS
  // =====================================================

  const generateInsights = () => {
    if (!student) return [];

    const insights = [];

    const lateEntry = student.attendance?.find(
      (a) =>
        a.type === "Gate" &&
        a.in > "09:30"
    );

    if (lateEntry) {
      insights.push("You entered late today");
    }

    const messMiss =
      student.attendance?.filter(
        (a) => a.type === "Mess"
      );

    if (!messMiss?.length) {
      insights.push("You skipped meals");
    }

    return insights;
  };

  const insights = generateInsights();

  return (
    <div className={`${currentTheme.bg} ${currentTheme.text} space-y-5`}>

      {/* ===================================================== */}
      {/* TABS */}
      {/* ===================================================== */}

      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
        {ADMIN_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`
              flex items-center justify-center gap-2
              text-xs lg:text-sm
              min-w-[120px]
              px-2 py-1
              rounded-md
              border
              transition-all duration-200
              hover:text-white
              ${
                activeTab === tab
                  ? currentTheme.tabActive
                  : `${currentTheme.tabInactive} ${currentTheme.tabHover}`
              }
            `}
          >
            <span className="text-sm">{TAB_ICONS[tab]}</span>
            <span>{t(tab)}</span>
          </button>
        ))}
      </div>

      {/* ===================================================== */}
      {/* AI SIGNAL */}
      {/* ===================================================== */}

      {activeTab === "signal" && (
        <HAFHAI
          t={t}
          selectedLanguage={selectedLanguage}
          role={userRole}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* MESSAGES */}
      {/* ===================================================== */}

      {activeTab === "messages" && (
        <HAFHMessagesDisplay
          t={t}
          role={userRole}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* CONTACTS */}
      {/* ===================================================== */}

      {activeTab === "contacts" && (
        <HAFHContatList
          t={t}
          role={userRole}
          hostelCode={hostelCode}
          currentTheme={currentTheme}
        />
      )}

      {/* ===================================================== */}
      {/* APPLAUSE */}
      {/* ===================================================== */}

      {activeTab === "applause" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("applause")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              Applause is a structured way of reinforcing
              positive behavior, effort, and growth.
            </p>

            <p className={paragraphClass}>
              Recognition builds confidence, emotional
              stability, and motivation.
            </p>

            <p className={paragraphClass}>
              Self-applause encourages resilience and
              self-improvement.
            </p>

          </div>

          <button
            onClick={() =>
              navigate("/hafhapplause")
            }
            className={buttonClass}
          >
            Go for Applauses
          </button>

        </div>

      )}

      {/* ===================================================== */}
      {/* RCA */}
      {/* ===================================================== */}

      {activeTab === "rca" && (

        <div className={cardClass}>

          <h2 className={titleClass}>
            {t("rca")}
          </h2>

          <div className="space-y-3 mt-3">

            <p className={paragraphClass}>
              Think5Whys RCA uncovers the real drivers
              behind problems instead of stopping at
              surface symptoms.
            </p>

            <p className={paragraphClass}>
              It improves clarity, deep reasoning,
              and structured decision-making.
            </p>

          </div>

          <button
            onClick={() =>
              navigate("/hafhrca")
            }
            className={buttonClass}
          >
            RCA / Causal Dashboard
          </button>

        </div>

      )}
    </div>
  );
};

export default HAFHAdmin;